import { Button, Col, Divider, Modal, Row, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import moment from "moment";
import FieldBuilder from "../../form-components/field-builder";
import EndPoints from "../../../services/end-points";
import { IExportTripFinancialReportRequest } from "../../../models/trip/request";

interface IProps {
  visible: boolean;
  toggleVisible: (visible: boolean) => void;
  onSubmit: (data: IExportTripFinancialReportRequest) => Promise<void>;
  loading: boolean;
}

interface IFormData {
  startDate: string;
  startTime?: string;
  endDate: string;
  endTime?: string;
  driverId?: number;
  storeId?: number;
}

const ExportFinancialReportModal: React.FC<IProps> = ({
  visible,
  toggleVisible,
  onSubmit,
  loading,
}) => {
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState<{ label: string; value: number }[]>([]);
  const [stores, setStores] = useState<{ label: string; value: number }[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(false);

  const methods = useForm<IFormData>({
    defaultValues: {
      startDate: "",
      startTime: "00:00",
      endDate: "",
      endTime: "23:59",
      driverId: undefined,
      storeId: undefined,
    },
  });

  const { handleSubmit, reset, setError, formState: { errors } } = methods;

  useEffect(() => {
    const fetchDrivers = async () => {
      setDriversLoading(true);
      try {
        const response = await EndPoints.deliveryWorkers.getAllDeliveryWorkers({
          light: true,
          perPage: -1,
        });
        setDrivers(
          response.data.data.map((driver: any) => ({
            label: `${driver.firstName} ${driver.lastName}`,
            value: driver.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching drivers:", error);
      } finally {
        setDriversLoading(false);
      }
    };

    const fetchStores = async () => {
      setStoresLoading(true);
      try {
        const response = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
        });
        setStores(
          response.data.data.map((store: any) => ({
            label: store.name,
            value: store.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching stores:", error);
      } finally {
        setStoresLoading(false);
      }
    };

    if (visible) {
      fetchDrivers();
      fetchStores();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      reset();
    }
  }, [visible, reset]);

  const handleFormSubmit = async (data: IFormData) => {
    // Validate date range
    if (data.startDate && data.endDate) {
      const start = moment(data.startDate);
      const end = moment(data.endDate);
      if (end.isBefore(start)) {
        setError("endDate", {
          type: "manual",
          message: t("end_date_must_be_after_start_date"),
        });
        return;
      }
    }

    // Build filters array
    const filters: Array<{ name: "driverId" | "storeId"; value: number }> = [];

    if (data.driverId) {
      filters.push({ name: "driverId", value: data.driverId });
    }
    if (data.storeId) {
      filters.push({ name: "storeId", value: data.storeId });
    }

    const requestData: IExportTripFinancialReportRequest = {
      startDate: moment(data.startDate).format("YYYY-MM-DD"),
      startTime: data.startTime || "00:00",
      endDate: moment(data.endDate).format("YYYY-MM-DD"),
      endTime: data.endTime || "23:59",
      filters: filters.length > 0 ? filters : undefined,
    };

    try {
      await onSubmit(requestData);
      toggleVisible(false);
    } catch (error) {
      // Error handling is done in context
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id="export-financial-report-form"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <Modal
          title={t("export_financial_report")}
          open={visible}
          closable
          destroyOnClose
          onCancel={() => toggleVisible(false)}
          afterClose={() => toggleVisible(false)}
          width="50vw"
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => toggleVisible(false)}
                style={{ marginInlineEnd: 8 }}
              >
                {t("cancel")}
              </Button>
              <Button
                loading={loading}
                htmlType="submit"
                form="export-financial-report-form"
                type="primary"
              >
                {t("export")}
              </Button>
            </div>
          }
        >
          <Divider style={{ margin: "10px 0" }} />
          <Spin spinning={loading}>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={12}>
                <FieldBuilder
                  name="startDate"
                  label={t("start_date")}
                  width="large"
                  rules={{ required: true }}
                  input={{
                    type: "date-picker",
                    format: "YYYY-MM-DD",
                  }}
                />
              </Col>

              <Col xs={24} sm={24} md={12}>
                <FieldBuilder
                  name="startTime"
                  label={t("start_time")}
                  width="large"
                  input={{
                    type: "time-picker",
                    format: "HH:mm",
                  }}
                />
              </Col>

              <Col xs={24} sm={24} md={12}>
                <FieldBuilder
                  name="endDate"
                  label={t("end_date")}
                  width="large"
                  rules={{ required: true }}
                  input={{
                    type: "date-picker",
                    format: "YYYY-MM-DD",
                  }}
                />
              </Col>

              <Col xs={24} sm={24} md={12}>
                <FieldBuilder
                  name="endTime"
                  label={t("end_time")}
                  width="large"
                  input={{
                    type: "time-picker",
                    format: "HH:mm",
                  }}
                />
              </Col>

              <Col xs={24} sm={24} md={12}>
                <FieldBuilder
                  name="driverId"
                  label={t("driver")}
                  width="large"
                  input={{
                    type: "select",
                    allowSearch: true,
                    options: drivers,
                    loading: driversLoading,
                  }}
                />
              </Col>

              <Col xs={24} sm={24} md={12}>
                <FieldBuilder
                  name="storeId"
                  label={t("store")}
                  width="large"
                  input={{
                    type: "select",
                    allowSearch: true,
                    options: stores,
                    loading: storesLoading,
                  }}
                />
              </Col>
            </Row>
          </Spin>
        </Modal>
      </form>
    </FormProvider>
  );
};

export default ExportFinancialReportModal;
